import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc
} from 'firebase/firestore';
import {
  Typography, Box, Paper, Chip, IconButton, Button,
  List, ListItem, ListItemText, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import 'moment/locale/fr';
import {
  fetchDanceStyles,
  getDanceStyleLabel,
} from '../services/danceStylesService';

moment.locale('fr');

function CourseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [danceStyleOptions, setDanceStyleOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourseAndRegistrations = async () => {
      setLoading(true);
      try {
        const styles = await fetchDanceStyles();
        setDanceStyleOptions(styles);

        // 1. Charger le cours
        const courseRef = doc(db, 'dance_courses', id);
        const courseSnap = await getDoc(courseRef);
        if (!courseSnap.exists()) throw new Error('Cours non trouvé');
        const courseData = { id: courseSnap.id, ...courseSnap.data() };
        setCourse(courseData);

        // 2. Charger les inscriptions liées à ce cours
        const registrationsRef = collection(db, 'registrations');
        const q = query(registrationsRef, where('courseId', '==', id));
        const registrationsSnap = await getDocs(q);

        const allStudents = [];

        registrationsSnap.forEach(doc => {
          const data = doc.data();
          if (Array.isArray(data.persons)) {
            data.persons.forEach(person => {
              allStudents.push({
                id: `${doc.id}-${person.firstName}-${person.lastName}`, // identifiant unique
                firstName: person.firstName,
                lastName: person.lastName
              });
            });
          }
        });

        setStudents(allStudents);

        // 3. Charger la liste d'attente de ce cours (rempli côté public quand
        // le cours est marqué "complet" — voir Registration.js)
        try {
          const waitlistSnap = await getDocs(query(collection(db, 'waitlist'), where('courseId', '==', id)));
          setWaitlist(waitlistSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (waitlistError) {
          console.error('Erreur chargement liste d\'attente:', waitlistError);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données :', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseAndRegistrations();
  }, [id]);

  const formatDate = (dateObj) => {
    if (!dateObj?.date) return 'Date inconnue';
    try {
      const date = dateObj.date.toDate ? dateObj.date.toDate() : new Date(dateObj.date);
      return moment(date).format('dddd DD MMM [à] HH:mm');
    } catch {
      return 'Date invalide';
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;
  if (!course) return <Typography>Cours non trouvé</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/courses')}>
        Retour à la liste
      </Button>

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {course.title}
        {course.isNewCourse && (
          <Chip label="🆕 Nouveau cours — Offre été 199€" color="warning" sx={{ fontWeight: 'bold' }} />
        )}
        {course.isFull && (
          <Chip label="🔒 Cours complet" color="error" sx={{ fontWeight: 'bold' }} />
        )}
      </Typography>
      <Typography variant="subtitle1" gutterBottom>{course.description}</Typography>
      <Typography variant="body2"><strong>Lieu :</strong> {course.location?.name || course.lieu || 'Non renseigné'}</Typography>
      <Typography variant="body2"><strong>Niveaux :</strong> {course.difficultyLevels?.join(', ')}</Typography>
      <Typography variant="body2"><strong>Public :</strong> {course.audiences?.join(', ') || 'Non renseigné'}</Typography>
      <Typography variant="body2"><strong>Styles de danse :</strong> {(course.danceStyles || []).map((styleId) => getDanceStyleLabel(danceStyleOptions, styleId)).join(', ') || 'Non renseigné'}</Typography>
      <Typography variant="body2"><strong>Durée :</strong> {course.duration} minutes</Typography>
      <Typography variant="body2"><strong>Nombre max :</strong> {course.maxStudents} élèves</Typography>

      <Typography variant="h6" sx={{ mt: 3 }}>Professeurs</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {(course.teachers || []).map((t, i) => (
          <Chip key={i} label={t.pseudo || '—'} />
        ))}
      </Box>

      <Typography variant="h6" sx={{ mt: 3 }}>Dates</Typography>
      <Paper sx={{ p: 2, mt: 1, maxHeight: 200, overflowY: 'auto' }}>
        {(course.dates || []).map((d, i) => (
          <Typography key={i} variant="body2">
            {formatDate(d)}
          </Typography>
        ))}
      </Paper>

      {/* Liste des élèves inscrits */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Élèves inscrits ({students.length})
      </Typography>

      {students.length > 0 ? (
        <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {students.map((student, index) => (
            <React.Fragment key={student.id}>
              <ListItem>
                <ListItemText
                  primary={`${student.firstName} ${student.lastName}`}
                />
              </ListItem>
              {index < students.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          Aucun élève inscrit pour le moment.
        </Typography>
      )}

      {/* Liste d'attente (cours marqué complet) */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Liste d'attente ({waitlist.length})
      </Typography>

      {waitlist.length > 0 ? (
        <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {waitlist.map((entry, index) => (
            <React.Fragment key={entry.id}>
              <ListItem>
                <ListItemText
                  primary={`${entry.firstName || ''} ${entry.lastName || ''}`.trim() || '—'}
                  secondary={[entry.email, entry.phone].filter(Boolean).join(' · ')}
                />
              </ListItem>
              {index < waitlist.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          Personne en liste d'attente pour le moment.
        </Typography>
      )}
    </Box>
  );
}

export default CourseDetails;